// src/modules/tenant/AssetIngestion.tsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { API_ROOT_URL, API_ORIGIN, API_V1_BASE_URL } from "../../config/api";
import {
  clearAssetUploadFailures,
  recordAssetUploadFailure,
} from "../../utils/assetFailureEscalation";

// Types
export interface AssetIngestionConfig {
  allowedFileTypes: Record<string, string>;
  maxFileSize: number;
  maxTotalSize: number;
  maxConcurrentUploads: number;
  enableRetry: boolean;
  maxRetries: number;
  retryDelay: number;
  enableDownstreamProcessing: boolean;
  downstreamEndpoint?: string;
  enableThumbnailGeneration: boolean;
  chunkSize: number;
  useChunkedUpload: boolean;
  enableResumableUploads: boolean;
}

export interface UploadFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  status:
    | "pending"
    | "uploading"
    | "processing"
    | "success"
    | "error"
    | "cancelled"
    | "paused"
    | "malware_detected"
    | "unsupported";
  progress: number;
  uploadedChunks: number;
  totalChunks: number;
  error?: string;
  uploadedUrl?: string;
  thumbnail?: string;
  metadata?: Record<string, any>;
  startTime?: number;
  endTime?: number;
  speed?: number;
  etag?: string;
  uploadId?: string;
  assetId?: string;
  scanResult?: {
    isInfected: boolean;
    threats?: string[];
    score?: number;
  };
  processingStatus?: {
    step: string;
    progress: number;
    message: string;
  };
  rejectionReason?: string;
  rejectionDetails?: {
    code: string;
    message: string;
    suggestion?: string;
  };
}

export interface UploadJob {
  id: string;
  fileName: string;
  originalName: string;
  fileSize: number;
  fileType: string;
  status:
    | "queued"
    | "uploading"
    | "processing"
    | "completed"
    | "failed"
    | "cancelled";
  progress: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  error?: string;
  outputUrl?: string;
  metadata?: Record<string, any>;
  processingSteps?: ProcessingStep[];
  uploadSpeed?: number;
}

export interface ProcessingStep {
  name: string;
  status: "pending" | "processing" | "completed" | "failed";
  startedAt?: string;
  completedAt?: string;
  error?: string;
}

export interface IngestionEvent {
  type:
    | "start"
    | "progress"
    | "complete"
    | "error"
    | "cancel"
    | "pause"
    | "resume";
  fileId?: string;
  jobId?: string;
  message?: string;
  data?: any;
  timestamp: Date;
}

interface AssetIngestionProps {
  onUploadComplete?: (files: UploadFile[]) => void;
  onUploadError?: (error: Error, file?: UploadFile) => void;
  onProgress?: (progress: number, file?: UploadFile) => void;
  onEvent?: (event: IngestionEvent) => void;
  config?: Partial<AssetIngestionConfig>;
  userId?: string;
  userEmail?: string;
  workspaceId?: string;
  orgName?: string;
  uploadEndpoint?: string;
  processEndpoint?: string;
  jobsEndpoint?: string;
  campaignsEndpoint?: string;
  eventsEndpoint?: string;
  resolveEventsEndpoint?: string;
  mappingEndpoint?: string;
  linkTreeEndpoint?: string;
  showHistory?: boolean;
  showMetadata?: boolean;
  allowMultiple?: boolean;
  autoStart?: boolean;
  enableChunking?: boolean;
  className?: string;
  dropzoneClassName?: string;
  compact?: boolean;
  onFileAdded?: (files: UploadFile[]) => void;
  onFileRemoved?: (fileId: string) => void;
  onJobStatusChange?: (job: UploadJob) => void;
}

// Global upload store for persistence across navigation
class UploadStore {
  private listeners: Set<(files: UploadFile[]) => void> = new Set();
  private files: UploadFile[] = [];
  private storageKey = "asset_ingestion_uploads";

  constructor() {
    const saved = sessionStorage.getItem(this.storageKey);
    if (saved) {
      try {
        this.files = JSON.parse(saved);
      } catch (e) {
        console.error("Failed to load saved uploads", e);
      }
    }
    this.saveToStorage();
  }

  private saveToStorage() {
    sessionStorage.setItem(this.storageKey, JSON.stringify(this.files));
  }

  getFiles(): UploadFile[] {
    return this.files;
  }

  updateFile(fileId: string, updates: Partial<UploadFile>) {
    this.files = this.files.map((f) =>
      f.id === fileId ? { ...f, ...updates } : f,
    );
    this.saveToStorage();
    this.notifyListeners();
  }

  addFiles(newFiles: UploadFile[]) {
    this.files = [...this.files, ...newFiles];
    this.saveToStorage();
    this.notifyListeners();
  }

  removeFile(fileId: string) {
    this.files = this.files.filter((f) => f.id !== fileId);
    this.saveToStorage();
    this.notifyListeners();
  }

  subscribe(listener: (files: UploadFile[]) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners() {
    this.listeners.forEach((listener) => listener(this.files));
  }

  getActiveUploads(): UploadFile[] {
    return this.files.filter(
      (f) =>
        f.status === "uploading" ||
        f.status === "pending" ||
        f.status === "processing",
    );
  }

  getUnsupportedFiles(): UploadFile[] {
    return this.files.filter((f) => f.status === "unsupported");
  }
}

const uploadStore = new UploadStore();

// Helper functions
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const generateFileId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

interface MappingOption {
  id: string;
  label: string;
  hint?: string;
}

const getTenantId = (): string => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return u?.tenantId ? String(u.tenantId) : "";
  } catch {
    return "";
  }
};

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("accessToken");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const getStoredUserId = (fallback: string): string => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return u?.id ? String(u.id) : fallback;
  } catch {
    return fallback;
  }
};

const getLocalToday = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const makeEmptyQuickForm = () => ({
  title: "",
  description: "",
  notes: "",
  startDate: getLocalToday(),
  endDate: "",
  status: "Active",
  tags: [] as string[],
  image: "",
  video: "",
  eventIds: [] as string[], // campaign only: events to link
  assetIds: [] as string[], // campaign only: assets to link
  linkCampaignIds: [] as string[], // event only: campaigns to link
});

interface MultiSelectDropdownProps {
  label: string;
  placeholder: string;
  options: MappingOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  // Short helper/feedback line shown under the field
  note?: string | null;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  placeholder,
  options,
  selected,
  onChange,
  disabled,
  note,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const selectedLabels = options
    .filter((o) => selected.includes(o.id))
    .map((o) => o.label);

  const q = search.trim().toLowerCase();
  const shown = q
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          (o.hint || "").toLowerCase().includes(q),
      )
    : options;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const toggle = (id: string) =>
    onChange(
      selected.includes(id)
        ? selected.filter((s) => s !== id)
        : [...selected, id],
    );

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {selected.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 text-[11px] rounded-full bg-blue-100 text-blue-700">
              {selected.length}
            </span>
          )}
        </label>
        {selected.length > 0 && !disabled && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-xs text-gray-500 hover:text-gray-800 hover:underline"
          >
            Clear
          </button>
        )}
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        title={selectedLabels.join(", ")}
        aria-expanded={open}
        className={`w-full flex items-center justify-between gap-2 text-left px-3 py-2 border rounded-lg bg-white text-sm transition disabled:opacity-50 disabled:cursor-not-allowed ${
          open
            ? "border-blue-500 ring-2 ring-blue-100"
            : "border-gray-300 hover:border-gray-400"
        }`}
      >
        {selectedLabels.length === 0 ? (
          <span className="text-gray-400 truncate">{placeholder}</span>
        ) : (
          <span className="block truncate text-gray-800">
            {selectedLabels.join(", ")}
          </span>
        )}
        <span
          className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        >
          ▾
        </span>
      </button>
      {note && <p className="mt-1.5 text-xs text-blue-700">{note}</p>}
      {open && (
        <div className="absolute z-40 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {options.length > 5 && (
            <div className="p-2 border-b border-gray-100">
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}...`}
                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
              />
            </div>
          )}
          <div className="max-h-60 overflow-y-auto">
            {options.length === 0 && (
              <p className="p-3 text-sm text-gray-400">No options available</p>
            )}
            {options.length > 0 && shown.length === 0 && (
              <p className="p-3 text-sm text-gray-400">No matches</p>
            )}
            {shown.map((opt) => {
              const checked = selected.includes(opt.id);
              return (
                <label
                  key={opt.id}
                  className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${
                    checked ? "bg-blue-50" : "hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(opt.id)}
                  />
                  <span className={`truncate ${checked ? "font-medium text-gray-900" : ""}`}>
                    {opt.label}
                  </span>
                  {opt.hint && (
                    <span className="ml-auto pl-2 text-xs text-gray-400 truncate max-w-[50%]">
                      {opt.hint}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Unsupported Files Section Component
interface UnsupportedFilesSectionProps {
  files: UploadFile[];
  onRemove: (fileId: string) => void;
  onRetry: (file: UploadFile) => void;
}

const UnsupportedFilesSection: React.FC<UnsupportedFilesSectionProps> = ({
  files,
  onRemove,
  onRetry,
}) => {
  if (files.length === 0) return null;

  return (
    <div className="mt-6 bg-red-50 border border-red-200 rounded-xl overflow-hidden">
      <div className="bg-red-100 px-6 py-3 border-b border-red-200">
        <div className="flex items-center gap-2">
          <span className="text-xl">⚠️</span>
          <h3 className="font-semibold text-red-800">
            Unsupported / Invalid Files ({files.length})
          </h3>
        </div>
        <p className="text-sm text-red-600 mt-1">
          These files could not be uploaded. Please review the errors and
          re-upload corrected versions.
        </p>
      </div>

      <div className="divide-y divide-red-200 max-h-80 overflow-y-auto">
        {files.map((file) => (
          <div key={file.id} className="p-4 hover:bg-red-50 transition">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="text-2xl">
                    {file.type.startsWith("image/") && "🖼️"}
                    {file.type.startsWith("video/") && "🎥"}
                    {file.type.startsWith("audio/") && "🎵"}
                    {file.type === "application/pdf" && "📄"}
                    {!file.type && "📁"}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800 truncate max-w-md">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>

                <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded-lg">
                  <div className="flex items-start gap-2">
                    <span className="text-red-600 text-sm">❌</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-800">
                        {file.rejectionReason || "Unsupported file format"}
                      </p>
                      {file.rejectionDetails && (
                        <>
                          <p className="text-xs text-red-600 mt-1">
                            {file.rejectionDetails.message}
                          </p>
                          {file.rejectionDetails.suggestion && (
                            <p className="text-xs text-red-500 mt-1">
                              💡 Suggestion: {file.rejectionDetails.suggestion}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => onRetry(file)}
                  className="text-blue-600 hover:text-blue-700 text-sm px-2 py-1 rounded border border-blue-300 hover:bg-blue-50"
                >
                  Retry
                </button>
                <button
                  onClick={() => onRemove(file.id)}
                  className="text-red-500 hover:text-red-700 text-xl leading-none"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Floating Progress Popup Component
interface FloatingProgressPopupProps {
  files: UploadFile[];
  onExpand: () => void;
  onCancel: (fileId: string) => void;
  onClose: () => void;
}

const FloatingProgressPopup: React.FC<FloatingProgressPopupProps> = ({
  files,
  onExpand,
  onCancel,
  onClose,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const activeFiles = files.filter(
    (f) =>
      f.status === "uploading" ||
      f.status === "pending" ||
      f.status === "processing",
  );
  const completedFiles = files.filter((f) => f.status === "success");
  const totalProgress =
    activeFiles.length > 0
      ? activeFiles.reduce((sum, f) => sum + f.progress, 0) / activeFiles.length
      : 100;

  if (activeFiles.length === 0 && completedFiles.length === 0) {
    return null;
  }

  if (isMinimized) {
    return (
      <div
        className="fixed bottom-4 right-4 bg-white rounded-full shadow-lg p-3 cursor-pointer z-50 hover:shadow-xl transition-all duration-200 animate-in fade-in slide-in-from-bottom-4"
        onClick={() => setIsMinimized(false)}
      >
        <div className="flex items-center gap-2">
          <div className="relative">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white text-xs">📤</span>
            </div>
            {activeFiles.length > 0 && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">
                  {activeFiles.length}
                </span>
              </div>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-800">
              {activeFiles.length > 0
                ? `${activeFiles.length} file(s) in progress`
                : "Upload complete!"}
            </p>
            {activeFiles.length > 0 && (
              <div className="w-24 mt-1">
                <div className="bg-gray-200 rounded-full h-1 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-purple-600 h-full transition-all duration-300"
                    style={{ width: `${totalProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600 ml-2"
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 w-96 bg-white rounded-xl shadow-2xl z-50 animate-in fade-in slide-in-from-bottom-4 border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-4 py-3 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className="text-white text-lg">📤</span>
          <h3 className="text-white font-semibold">Upload Progress</h3>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsMinimized(true)}
            className="text-white hover:text-gray-200"
          >
            ━
          </button>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            ×
          </button>
        </div>
      </div>

      <div className="p-4 max-h-96 overflow-y-auto">
        {activeFiles.length > 0 ? (
          <>
            <div className="mb-3">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Overall Progress</span>
                <span className="font-semibold text-blue-600">
                  {Math.round(totalProgress)}%
                </span>
              </div>
              <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-blue-500 to-purple-600 h-full transition-all duration-300"
                  style={{ width: `${totalProgress}%` }}
                />
              </div>
            </div>

            <div className="space-y-3">
              {activeFiles.map((file) => (
                <div key={file.id} className="bg-gray-50 rounded-lg p-2">
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800 truncate max-w-[200px]">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                    <button
                      onClick={() => onCancel(file.id)}
                      className="text-red-400 hover:text-red-600 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="mt-1">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-500">
                        {file.status === "uploading" && "Uploading"}
                        {file.status === "processing" &&
                          (file.processingStatus?.step || "Processing")}
                        {file.status === "pending" && "Pending"}
                      </span>
                      {file.status !== "pending" && (
                        <span className="text-gray-400">
                          {Math.round(file.progress)}%
                        </span>
                      )}
                    </div>
                    <div className="bg-gray-200 rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          file.status === "processing"
                            ? "bg-orange-500"
                            : "bg-blue-500"
                        }`}
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                    {file.processingStatus?.message && (
                      <p className="text-xs text-gray-500 mt-1">
                        {file.processingStatus.message}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          completedFiles.length > 0 && (
            <div className="text-center py-4">
              <div className="text-3xl mb-2">✅</div>
              <p className="text-green-600 font-medium">Upload Complete!</p>
              <p className="text-sm text-gray-500 mt-1">
                {completedFiles.length} file(s) uploaded successfully
              </p>
              <button
                onClick={onExpand}
                className="mt-3 px-4 py-1 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
              >
                View Details
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
};

// Optimized Progress polling hook with completion detection
const useProgressPolling = (
  fileIds: string[],
  onProgressUpdate?: (fileId: string, progress: any) => void,
) => {
  const [progresses, setProgresses] = useState<Record<string, any>>({});
  const pollingAttempts = useRef<Record<string, number>>({});
  const MAX_POLLING_ATTEMPTS = 60;
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  useEffect(() => {
    if (fileIds.length === 0) {
      // Clear interval if no files
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    const fetchProgress = async () => {
      for (const fileId of fileIds) {
        if (!pollingAttempts.current[fileId]) {
          pollingAttempts.current[fileId] = 0;
        }

        try {
          const response = await fetch(`/api/v1/assets/progress/${fileId}`);

          if (response.status === 404) {
            pollingAttempts.current[fileId] =
              (pollingAttempts.current[fileId] || 0) + 1;
            if (pollingAttempts.current[fileId] > MAX_POLLING_ATTEMPTS) {
              uploadStore.updateFile(fileId, {
                status: "error",
                processingStatus: {
                  step: "Progress unavailable",
                  progress: 0,
                  message: "Unable to verify upload progress.",
                },
              } as any);
              delete pollingAttempts.current[fileId];
            }
            continue;
          }

          const data = await response.json();

          if (data.success) {
            setProgresses((prev) => ({ ...prev, [fileId]: data.progress }));

            const progressValue =
              data.progress?.uploadProgress ||
              data.progress?.processingProgress ||
              0;
            const isComplete =
              data.type === "completed" ||
              progressValue >= 100 ||
              data.status === "completed";

            if (isComplete) {
              console.log(`File ${fileId} is complete!`);
              uploadStore.updateFile(fileId, {
                progress: 100,
                status: "success",
                endTime: Date.now(),
                processingStatus: {
                  step: "Complete",
                  progress: 100,
                  message: "Upload complete! File is ready.",
                },
              } as any);

              if (onProgressUpdate) {
                onProgressUpdate(fileId, {
                  ...data.progress,
                  isComplete: true,
                });
              }

              delete pollingAttempts.current[fileId];
              continue;
            }

            if (data.type === "uploading") {
              const uploadProgress = data.progress?.uploadProgress || 0;
              const displayProgress = Math.min(uploadProgress, 99);
              uploadStore.updateFile(fileId, {
                progress: displayProgress,
                speed: data.speed,
                status: "uploading",
                processingStatus: {
                  step: "Uploading",
                  progress: displayProgress,
                  message: `Uploading to server... ${Math.round(displayProgress)}%`,
                },
              } as any);
            } else if (data.type === "processing") {
              const processingProgress =
                data.progress?.processingProgress || 70;
              const displayProgress = Math.min(processingProgress, 99);

              uploadStore.updateFile(fileId, {
                progress: displayProgress,
                status: "processing",
                processingStatus: {
                  step: data.progress?.processingStep || "Processing",
                  progress: displayProgress,
                  message:
                    data.progress?.processingMessage ||
                    "Processing file on server...",
                },
              } as any);
            }

            onProgressUpdate?.(fileId, data.progress);
          }

          pollingAttempts.current[fileId] =
            (pollingAttempts.current[fileId] || 0) + 1;

          if (pollingAttempts.current[fileId] > MAX_POLLING_ATTEMPTS) {
            console.warn(
              `File ${fileId} exceeded max polling attempts, forcing completion`,
            );
            uploadStore.updateFile(fileId, {
              progress: 100,
              status: "success",
              processingStatus: {
                step: "Complete",
                progress: 100,
                message: "Upload complete! (auto-detected)",
              },
            } as any);
            delete pollingAttempts.current[fileId];
          }
        } catch (error) {
          console.error("Failed to fetch progress:", error);
          pollingAttempts.current[fileId] =
            (pollingAttempts.current[fileId] || 0) + 1;

          if (pollingAttempts.current[fileId] > MAX_POLLING_ATTEMPTS) {
            console.warn(`File ${fileId} polling timed out, assuming complete`);
            uploadStore.updateFile(fileId, {
              progress: 100,
              status: "success",
              processingStatus: {
                step: "Complete",
                progress: 100,
                message: "Upload complete! (timeout)",
              },
            } as any);
            delete pollingAttempts.current[fileId];
          }
        }
      }
    };

    fetchProgress();

    // Poll every 1.5 seconds
    pollingIntervalRef.current = setInterval(fetchProgress, 1500);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [fileIds.join(",")]);

  return progresses;
};

// Main Component
export const AssetIngestion: React.FC<AssetIngestionProps> = ({
  onUploadComplete,
  onUploadError,
  onProgress,
  onEvent,
  config: userConfig,
  userId = "anonymous",
  userEmail = "anonymous@example.com",
  workspaceId = "default",
  orgName = "SIMB/Bentley",
  uploadEndpoint = `${API_ROOT_URL}/v1/assets/ingest`,
  processEndpoint = `${API_ROOT_URL}/v1/assets/process`,
  jobsEndpoint = `${API_ROOT_URL}/v1/assets/jobs`,
  campaignsEndpoint = `${API_ROOT_URL}/v1/campaigns`,
  eventsEndpoint = `${API_ROOT_URL.replace(/\/api\/?$/, "")}/events`,
  resolveEventsEndpoint = `${API_ROOT_URL}/campaign-assets/resolve-events`,
  mappingEndpoint = `${API_ROOT_URL}/campaign-assets/map-uploaded`,
  linkTreeEndpoint = `${API_ROOT_URL}/asset-links/tree`,
  showHistory = true,
  showMetadata = true,
  allowMultiple = true,
  autoStart = false,
  enableChunking = false,
  className = "",
  dropzoneClassName = "",
  compact = false,
  onFileAdded,
  onFileRemoved,
  onJobStatusChange,
}) => {
  const MAX_SAFE_FILE_SIZE = 1024 * 1024 * 1024 * 10;
  const MAX_TOTAL_SIZE = 1024 * 1024 * 1024 * 50;

  const DEFAULT_CONFIG: AssetIngestionConfig = {
    allowedFileTypes: {
      "image/jpeg": ".jpg, .jpeg",
      "image/png": ".png",
      "image/gif": ".gif",
      "image/webp": ".webp",
      "image/svg+xml": ".svg",
      "video/mp4": ".mp4",
      "video/quicktime": ".mov",
      "video/x-msvideo": ".avi",
      "video/mpeg": ".mpeg, .mpg, .mpe, .m2v",
      "video/webm": ".webm",
      "audio/mpeg": ".mp3",
      "audio/wav": ".wav",
      "audio/ogg": ".ogg",
      "application/pdf": ".pdf",
      "application/msword": ".doc",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        ".docx",
      "text/plain": ".txt",
      "application/json": ".json",
      "application/zip": ".zip",
    },
    maxFileSize: MAX_SAFE_FILE_SIZE,
    maxTotalSize: MAX_TOTAL_SIZE,
    maxConcurrentUploads: 3,
    enableRetry: false,
    maxRetries: 1,
    retryDelay: 2000,
    enableDownstreamProcessing: false,
    enableThumbnailGeneration: false,
    chunkSize: 10 * 1024 * 1024,
    useChunkedUpload: false,
    enableResumableUploads: false,
  };

  const config: AssetIngestionConfig = { ...DEFAULT_CONFIG, ...userConfig };

  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [uploadJobs, setUploadJobs] = useState<UploadJob[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [activeTab, setActiveTab] = useState<"upload" | "history">("upload");
  const [showMetadataPanel, setShowMetadataPanel] = useState(false);
  const [selectedFileMetadata, setSelectedFileMetadata] =
    useState<UploadFile | null>(null);
  const [globalProgress, setGlobalProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState<{
    text: string;
    type: "info" | "success" | "error";
  } | null>(null);
  const [showFloatingPopup, setShowFloatingPopup] = useState(false);

  const [campaignOptions, setCampaignOptions] = useState<MappingOption[]>([]);
  const [eventOptions, setEventOptions] = useState<MappingOption[]>([]);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
  // event_id -> campaign_ids it belongs to (from campaign_events)
  const [eventCampaignMap, setEventCampaignMap] = useState<
    Record<string, string[]>
  >({});
  // Tells the user what the pickers changed for them (auto-filled / removed)
  const [mappingNote, setMappingNote] = useState<{
    field: "campaign" | "event";
    text: string;
  } | null>(null);
  const mappingNoteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [mappingPrompt, setMappingPrompt] = useState<
    | { kind: "none" }
    | {
        kind: "summary";
        campaigns: MappingOption[];
        eventLinks: {
          event: MappingOption;
          campaigns: MappingOption[];
        }[];
      }
    | { kind: "create"; entity: "campaign" | "event" }
    | null
  >(null);
  const [quickForm, setQuickForm] = useState(makeEmptyQuickForm());
  const [quickSaving, setQuickSaving] = useState(false);
  const [quickError, setQuickError] = useState("");
  const [quickTag, setQuickTag] = useState("");
  const [quickMediaTab, setQuickMediaTab] = useState<"image" | "video">(
    "image",
  );
  const [assetOptions, setAssetOptions] = useState<MappingOption[]>([]);
  const quickImageRef = useRef<HTMLInputElement>(null);
  const quickVideoRef = useRef<HTMLInputElement>(null);
  const mappingRef = useRef<{ campaignIds: string[]; eventIds: string[] }>({
    campaignIds: [],
    eventIds: [],
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllers = useRef<Map<string, AbortController>>(new Map());
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const isPollingRef = useRef(false);
  const lastRefreshTimeRef = useRef(0);
  const MIN_REFRESH_INTERVAL = 2000;

  const activeUploadIds = uploadFiles
    .filter(
      (f) =>
        f.status === "uploading" ||
        f.status === "pending" ||
        f.status === "processing",
    )
    .map((f) => f.id);
  const unsupportedFiles = uploadFiles.filter(
    (f) => f.status === "unsupported",
  );
  const validFiles = uploadFiles.filter((f) => f.status !== "unsupported");

  // Optimized refreshJobs with throttling
  const refreshJobs = useCallback(async () => {
    // Don't refresh if not showing history or if already polling
    if (activeTab !== "history" || isPollingRef.current) return;

    // Throttle: don't refresh more than once every 2 seconds
    const now = Date.now();
    if (now - lastRefreshTimeRef.current < MIN_REFRESH_INTERVAL) {
      return;
    }

    isPollingRef.current = true;
    lastRefreshTimeRef.current = now;

    try {
      const response = await fetch(`${jobsEndpoint}?userId=${userId}`);
      if (response.ok) {
        const data = await response.json();
        const newJobs = data.jobs || [];

        setUploadJobs((prevJobs) => {
          // Check if jobs actually changed
          const hasChanges =
            JSON.stringify(prevJobs) !== JSON.stringify(newJobs);
          if (hasChanges) {
            newJobs.forEach((job: UploadJob) => {
              onJobStatusChange?.(job);
            });
            return newJobs;
          }
          return prevJobs;
        });
      }
    } catch (error) {
      console.error("Error fetching upload jobs:", error);
    } finally {
      isPollingRef.current = false;
    }
  }, [jobsEndpoint, userId, activeTab, onJobStatusChange]);

  // Function to merge upload files with jobs for real-time display
  const getMergedJobs = useCallback((): UploadJob[] => {
    const activeUploadsMap = new Map();
    uploadFiles.forEach((file) => {
      if (
        file.status === "uploading" ||
        file.status === "pending" ||
        file.status === "processing" ||
        file.status === "error"
      ) {
        let status: "queued" | "uploading" | "processing" | "failed" = "queued";
        if (file.status === "uploading") status = "uploading";
        if (file.status === "processing") status = "processing";
        if (file.status === "error") status = "failed";

        activeUploadsMap.set(file.id, {
          id: file.id,
          originalName: file.name,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          status: status,
          progress: file.progress,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          error: file.error,
          metadata: file.processingStatus
            ? {
                processingStep: file.processingStatus.step,
                processingMessage: file.processingStatus.message,
                processingProgress: file.processingStatus.progress,
              }
            : undefined,
        });
      }
    });

    const mergedJobs = [...uploadJobs];

    activeUploadsMap.forEach((activeJob, id) => {
      const existingIndex = mergedJobs.findIndex((job) => job.id === id);
      if (existingIndex >= 0) {
        mergedJobs[existingIndex] = {
          ...mergedJobs[existingIndex],
          progress: activeJob.progress,
          status: activeJob.status,
          updatedAt: new Date().toISOString(),
          metadata: {
            ...mergedJobs[existingIndex].metadata,
            ...activeJob.metadata,
          },
        };
      } else {
        mergedJobs.unshift(activeJob);
      }
    });

    return mergedJobs
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
      .slice(0, 50);
  }, [uploadFiles, uploadJobs]);

  const displayJobs = getMergedJobs();

  // Smart polling with dynamic interval
  useEffect(() => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Only poll if on history tab
    if (activeTab !== "history") {
      return;
    }

    // Initial refresh
    refreshJobs();

    // Determine if we need to poll
    const hasActiveUploads = uploadFiles.some(
      (f) =>
        f.status === "uploading" ||
        f.status === "pending" ||
        f.status === "processing",
    );

    const hasActiveJobs = displayJobs.some(
      (j) => j.status === "uploading" || j.status === "processing",
    );

    // Set polling interval based on activity
    const shouldPollFrequently = hasActiveUploads || hasActiveJobs;
    const POLL_INTERVAL = shouldPollFrequently ? 3000 : 15000;

    console.log(
      `📊 Setting polling interval to ${POLL_INTERVAL}ms (${shouldPollFrequently ? "active" : "idle"})`,
    );

    // Start polling
    pollingIntervalRef.current = setInterval(() => {
      refreshJobs();
    }, POLL_INTERVAL);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [activeTab, uploadFiles, displayJobs, refreshJobs]);

  // Subscribe to global store
  useEffect(() => {
    const unsubscribe = uploadStore.subscribe((storedFiles) => {
      setUploadFiles(storedFiles);
      updateGlobalProgress(storedFiles);

      const hasActiveUploads = storedFiles.some(
        (f) =>
          f.status === "uploading" ||
          f.status === "pending" ||
          f.status === "processing",
      );
      setIsUploading(hasActiveUploads);

      // Only refresh if on history tab and there are active uploads
      if (activeTab === "history" && hasActiveUploads) {
        // Use a debounced refresh
        setTimeout(refreshJobs, 500);
      }

      const hasCompletedFiles = storedFiles.some((f) => f.status === "success");
      if (!hasActiveUploads && !hasCompletedFiles) {
        setShowFloatingPopup(false);
      }
    });

    setUploadFiles(uploadStore.getFiles());
    return () => {
      unsubscribe();
    };
  }, [activeTab, refreshJobs]);

  // Progress polling for real-time updates
  useProgressPolling(activeUploadIds, (fileId, progress) => {
    // Only refresh if progress indicates completion
    if (progress?.isComplete && activeTab === "history") {
      refreshJobs();
    }
  });

  const updateGlobalProgress = (files: UploadFile[]) => {
    if (files.length === 0) {
      setGlobalProgress(0);
      return;
    }
    const progress =
      files.reduce((sum, file) => sum + file.progress, 0) / files.length;
    setGlobalProgress(progress);
    onProgress?.(progress);
  };

  const validateFile = (
    file: File,
  ): { isValid: boolean; error?: string; rejectionDetails?: any } => {
    // Check file type
    const fileExt = file.name.split(".").pop()?.toLowerCase() || "";
    const allowedExtensions = Object.values(config.allowedFileTypes)
      .flatMap((value) => value.split(","))
      .map((value) => value.trim().replace(/^\./, "").toLowerCase());
    const isTypeAllowed =
      Object.keys(config.allowedFileTypes).some((type) =>
        file.type.match(new RegExp(type.replace("*", ".*"))),
      ) || allowedExtensions.includes(fileExt);

    if (!isTypeAllowed && Object.keys(config.allowedFileTypes).length > 0) {
      const supportedFormats = Object.values(config.allowedFileTypes).join(
        ", ",
      );

      return {
        isValid: false,
        error: `Unsupported file format: ${fileExt || file.type || "unknown"}`,
        rejectionDetails: {
          code: "UNSUPPORTED_FORMAT",
          message: `The file "${file.name}" uses a format that is not supported.`,
          suggestion: `Please convert your file to one of these supported formats: ${supportedFormats}`,
        },
      };
    }

    // Check file size
    if (file.size > config.maxFileSize) {
      return {
        isValid: false,
        error: `File size exceeds maximum allowed (${formatFileSize(config.maxFileSize)})`,
        rejectionDetails: {
          code: "FILE_TOO_LARGE",
          message: `The file "${file.name}" is too large (${formatFileSize(file.size)}).`,
          suggestion: `Please compress your file or split it into smaller parts. Maximum allowed size is ${formatFileSize(config.maxFileSize)}.`,
        },
      };
    }

    // Check for video codec issues (basic check)
    if (file.type.startsWith("video/")) {
      const unsupportedVideoCodecs = ["avi", "wmv", "flv", "mkv"];
      const fileExt = file.name.split(".").pop()?.toLowerCase() || "";

      if (unsupportedVideoCodecs.includes(fileExt)) {
        return {
          isValid: false,
          error: `Video codec not supported: ${fileExt.toUpperCase()}`,
          rejectionDetails: {
            code: "UNSUPPORTED_CODEC",
            message: `The video file "${file.name}" uses a codec that is not supported.`,
            suggestion: `Please convert your video to MP4 format with H.264 codec for best compatibility.`,
          },
        };
      }
    }

    // Check for audio codec issues
    if (file.type.startsWith("audio/")) {
      const unsupportedAudioCodecs = ["wma", "aac", "flac"];
      const fileExt = file.name.split(".").pop()?.toLowerCase() || "";

      if (unsupportedAudioCodecs.includes(fileExt)) {
        return {
          isValid: false,
          error: `Audio codec not supported: ${fileExt.toUpperCase()}`,
          rejectionDetails: {
            code: "UNSUPPORTED_CODEC",
            message: `The audio file "${file.name}" uses a codec that is not supported.`,
            suggestion: `Please convert your audio to MP3 format for best compatibility.`,
          },
        };
      }
    }

    return { isValid: true };
  };

  const uploadFileToBackend = async (
    uploadFile: UploadFile,
  ): Promise<UploadFile> => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("files", uploadFile.file);
      formData.append("userId", userId);
      formData.append("userEmail", userEmail);
      formData.append("workspaceId", workspaceId);
      formData.append("orgName", orgName);
      formData.append("organization", orgName);
      if (uploadFile.assetId) {
        formData.append("assetId", uploadFile.assetId);
      }
      try {
        const storedUser = JSON.parse(localStorage.getItem("user") || "null");
        if (storedUser?.tenantId) {
          formData.append("tenantId", String(storedUser.tenantId));
        }
      } catch {
        // no stored user; the backend will reject with a clear message
      }

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          const displayProgress = Math.min(percentComplete, 99);

          uploadStore.updateFile(uploadFile.id, {
            progress: displayProgress,
            status: "uploading",
            processingStatus: {
              step: "Uploading",
              progress: displayProgress,
              message: `Uploading to server... ${Math.round(displayProgress)}%`,
            },
          });

          onEvent?.({
            type: "progress",
            fileId: uploadFile.id,
            data: { progress: displayProgress },
            timestamp: new Date(),
          });
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            console.log("Upload response:", response);

            if (response.success && response.data?.successful?.length > 0) {
              const uploadedFileData = response.data.successful[0];

              // Check if the response indicates processing is complete
              const isComplete =
                uploadedFileData.status === "completed" ||
                uploadedFileData.processingComplete === true ||
                (uploadedFileData.url && !uploadedFileData.processingRequired);

              if (isComplete) {
                uploadStore.updateFile(uploadFile.id, {
                  status: "success",
                  progress: 100,
                  uploadedUrl: uploadedFileData.url,
                  endTime: Date.now(),
                  processingStatus: {
                    step: "Complete",
                    progress: 100,
                    message: "Upload complete! File is ready.",
                  },
                });

                resolve({
                  ...uploadFile,
                  status: "success",
                  progress: 100,
                  uploadedUrl: uploadedFileData.url,
                  assetId: String(
                    uploadedFileData.parentAssetId ??
                      uploadedFileData.assetId ??
                      uploadedFileData.asset_id ??
                      "",
                  ),
                  endTime: Date.now(),
                  scanResult: {
                    isInfected: false,
                    score: uploadedFileData.securityScore || 0,
                  },
                });
              } else {
                uploadStore.updateFile(uploadFile.id, {
                  status: "processing",
                  progress: 70,
                  uploadedUrl: uploadedFileData.url,
                  processingStatus: {
                    step: "Processing",
                    progress: 70,
                    message:
                      "Processing file on server... This may take a moment.",
                  },
                });

                resolve({
                  ...uploadFile,
                  status: "processing",
                  progress: 70,
                  uploadedUrl: uploadedFileData.url,
                  assetId: String(
                    uploadedFileData.parentAssetId ??
                      uploadedFileData.assetId ??
                      uploadedFileData.asset_id ??
                      "",
                  ),
                  endTime: Date.now(),
                  scanResult: {
                    isInfected: false,
                    score: uploadedFileData.securityScore || 0,
                  },
                });
              }
            } else if (response.data?.failed?.length > 0) {
              const errorMsg = response.data.failed[0].error || "Upload failed";
              reject(new Error(errorMsg));
            } else {
              reject(new Error(response.message || "Upload failed"));
            }
          } catch (e) {
            reject(new Error("Invalid response from server"));
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Network error"));
      });

      xhr.addEventListener("abort", () => {
        reject(new Error("Upload cancelled"));
      });

      abortControllers.current.set(uploadFile.id, {
        abort: () => xhr.abort(),
      } as AbortController);

      xhr.open("POST", uploadEndpoint);
      xhr.setRequestHeader("X-File-Id", uploadFile.id);
      const accessToken = localStorage.getItem("accessToken");
      if (accessToken) {
        xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
      }
      xhr.send(formData);
    });
  };

  const resetMappingSelection = () => {
    setSelectedCampaignIds([]);
    setSelectedEventIds([]);
  };

  const mapAssetToTargets = async (uploaded: UploadFile) => {
    const { campaignIds, eventIds } = mappingRef.current;
    if (campaignIds.length === 0 && eventIds.length === 0) return;

    if (!uploaded.assetId) {
      setStatusMessage({
        text: `${uploaded.name} was uploaded but could not be mapped (no asset id returned).`,
        type: "error",
      });
      return;
    }

    // The asset row can lag behind the upload response; retry a few times on 404
    for (let attempt = 1; attempt <= 15; attempt++) {
      try {
        const res = await fetch(mappingEndpoint, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            asset_id: uploaded.assetId,
            campaign_ids: campaignIds,
            event_ids: eventIds,
            created_by: userId,
            tenant_id: getTenantId() || undefined,
          }),
        });
        if (res.ok) return;
        if (res.status !== 404) break;
      } catch (err) {
        console.error("Asset mapping failed", err);
      }
      await new Promise((r) => setTimeout(r, 2000));
    }

    setStatusMessage({
      text: `${uploaded.name} was uploaded, but mapping to campaigns/events failed. You can map it manually.`,
      type: "error",
    });
  };

  const uploadWithRetry = async (
    uploadFile: UploadFile,
  ): Promise<UploadFile> => {
    uploadStore.updateFile(uploadFile.id, {
      status: "uploading",
      startTime: Date.now(),
    });

    try {
      const uploadedFile = await uploadFileToBackend(uploadFile);
      clearAssetUploadFailures(`tenant:${uploadFile.id}`);
      await mapAssetToTargets(uploadedFile); // never throws, so a mapping failure doesn't mark the upload failed
      return uploadedFile;
    } catch (error) {
      const uploadError =
        error instanceof Error ? error : new Error("Upload failed");
      uploadStore.updateFile(uploadFile.id, {
        status: "error",
        progress: 0,
        error: uploadError.message,
        endTime: Date.now(),
        processingStatus: {
          step: "Failed",
          progress: 0,
          message: uploadError.message,
        },
      });
      const attempts = recordAssetUploadFailure(`tenant:${uploadFile.id}`, {
        fileName: uploadFile.name,
        assetId: uploadFile.assetId,
        trackingId: uploadFile.id,
        error: uploadError.message,
        source: "Tenant Asset Ingestion",
      });
      if (attempts < 3) {
        setStatusMessage({
          text: `Upload failed for ${uploadFile.name} (attempt ${attempts} of 3). Use Retry to try again.`,
          type: "error",
        });
      }
      throw uploadError;
    }
  };

  const retryFailedFile = async (file: UploadFile) => {
    if (isUploading) return;
    setIsUploading(true);
    setShowFloatingPopup(true);
    try {
      const uploadedFile = await uploadWithRetry(file);
      onUploadComplete?.([uploadedFile]);
      setStatusMessage({
        text: `✓ Successfully uploaded ${file.name}`,
        type: "success",
      });
      resetMappingSelection();
    } catch (error) {
      onUploadError?.(
        error instanceof Error ? error : new Error("Upload failed"),
        file,
      );
    } finally {
      setIsUploading(false);
    }
  };

  // Builds + opens the confirmation popup for a given selection.
  // Returns false if the popup could not be opened (event resolution failed).
  const openMappingPrompt = async (
    campaignIds: string[],
    eventIds: string[],
    campaignOpts: MappingOption[] = campaignOptions,
    eventOpts: MappingOption[] = eventOptions,
  ): Promise<boolean> => {
    // Nothing selected -> legal notice
    if (campaignIds.length === 0 && eventIds.length === 0) {
      setMappingPrompt({ kind: "none" });
      return true;
    }

    // Build the summary (resolve which campaigns the selected events belong to)
    const campaigns = campaignOpts.filter((c) => campaignIds.includes(c.id));
    const eventLinks: { event: MappingOption; campaigns: MappingOption[] }[] =
      [];

    if (eventIds.length > 0) {
      try {
        const res = await fetch(resolveEventsEndpoint, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            event_ids: eventIds,
            tenant_id: getTenantId() || undefined,
          }),
        });
        const json = await res.json();
        const rows: any[] = json.data || [];

        eventIds.forEach((eid) => {
          const event = eventOpts.find((e) => e.id === eid) || {
            id: eid,
            label: eid,
          };
          const linked = rows
            .filter((r) => String(r.event_id) === eid)
            .map((r) => ({
              id: String(r.campaign_id),
              label: r.campaign_title || String(r.campaign_id),
            }));
          eventLinks.push({ event, campaigns: linked });
        });
      } catch (err) {
        console.error("Failed to resolve event campaigns", err);
        setStatusMessage({
          text: "Could not verify the campaigns linked to the selected events. Please try again.",
          type: "error",
        });
        return false;
      }
    }

    setMappingPrompt({ kind: "summary", campaigns, eventLinks });
    return true;
  };

  const showMappingNote = (field: "campaign" | "event", text: string) => {
    if (mappingNoteTimer.current) clearTimeout(mappingNoteTimer.current);
    setMappingNote({ field, text });
    mappingNoteTimer.current = setTimeout(() => setMappingNote(null), 6000);
  };

  const quoteList = (names: (string | undefined)[]) =>
    names
      .filter(Boolean)
      .map((n) => `"${n}"`)
      .join(", ");

  // Picking a campaign narrows events; dropping one removes events no longer covered
  const handleCampaignSelection = (ids: string[]) => {
    const kept = selectedEventIds.filter((eid) => {
      const linked = eventCampaignMap[eid] || [];
      return linked.length === 0 || linked.some((c) => ids.includes(c));
    });
    const removed = selectedEventIds.filter((eid) => !kept.includes(eid));
    setSelectedCampaignIds(ids);
    setSelectedEventIds(kept);
    if (removed.length) {
      const names = removed.map((eid) => eventOptions.find((e) => e.id === eid)?.label);
      showMappingNote(
        "event",
        `Removed ${quoteList(names)} — not part of the selected campaigns.`,
      );
    } else {
      setMappingNote(null);
    }
  };

  // Picking an event fills in its campaign(s) unless one is already selected
  const handleEventSelection = (ids: string[]) => {
    const added = ids.filter((id) => !selectedEventIds.includes(id));
    const nextCampaigns = [...selectedCampaignIds];
    added.forEach((eid) => {
      const linked = eventCampaignMap[eid] || [];
      if (linked.length && !linked.some((c) => nextCampaigns.includes(c))) {
        linked.forEach((c) => {
          if (!nextCampaigns.includes(c)) nextCampaigns.push(c);
        });
      }
    });
    const autoAdded = nextCampaigns.filter((c) => !selectedCampaignIds.includes(c));
    setSelectedCampaignIds(nextCampaigns);
    setSelectedEventIds(ids);
    if (autoAdded.length) {
      const names = autoAdded.map((c) => campaignOptions.find((o) => o.id === c)?.label);
      showMappingNote(
        "campaign",
        `Added ${quoteList(names)} automatically for the selected event.`,
      );
    }
  };

  const campaignLabelById = new Map(campaignOptions.map((c) => [c.id, c.label]));
  const visibleEventOptions: MappingOption[] = eventOptions
    .filter(
      (e) =>
        selectedCampaignIds.length === 0 ||
        selectedEventIds.includes(e.id) ||
        (eventCampaignMap[e.id] || []).some((c) =>
          selectedCampaignIds.includes(c),
        ),
    )
    .map((e) => {
      const names = (eventCampaignMap[e.id] || [])
        .map((c) => campaignLabelById.get(c))
        .filter(Boolean);
      return { ...e, hint: names.join(", ") };
    });

  const startUpload = async () => {
    const pendingFiles = uploadFiles.filter((f) => f.status === "pending");

    if (pendingFiles.length === 0) {
      setStatusMessage({ text: "No files to upload", type: "error" });
      setTimeout(() => setStatusMessage(null), 3000);
      return;
    }

    await openMappingPrompt(selectedCampaignIds, selectedEventIds);
  };

  const loadAssetOptions = async () => {
    try {
      const res = await fetch(
        `${API_ROOT_URL}/assets?only_product_mapped=true&limit=200&skip_preview=true`,
        { headers: authHeaders() },
      );
      if (!res.ok) throw new Error(`Request failed with ${res.status}`);
      const json = await res.json();
      const list = json.data || json;
      setAssetOptions(
        list.map((a: any) => ({
          id: String(a.id),
          label: a.original_filename,
        })),
      );
    } catch (err) {
      console.error("Failed to load assets for quick create", err);
    }
  };

  const openQuickCreate = (entity: "campaign" | "event") => {
    setQuickForm({
      ...makeEmptyQuickForm(),
      status: entity === "campaign" ? "Active" : "Scheduled",
    });
    setQuickError("");
    setQuickTag("");
    setQuickMediaTab("image");
    if (entity === "campaign") loadAssetOptions();
    setMappingPrompt({ kind: "create", entity });
  };

  const addQuickTag = () => {
    // Accept comma-separated input: "tag1,tag2,tag3" becomes 3 tags
    const incoming = quickTag
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    if (incoming.length === 0) return;

    setQuickForm((p) => {
      const merged = [...p.tags];
      incoming.forEach((t) => {
        if (!merged.includes(t)) merged.push(t);
      });
      return { ...p, tags: merged };
    });
    setQuickTag("");
  };

  const removeQuickTag = (tag: string) =>
    setQuickForm((p) => ({ ...p, tags: p.tags.filter((t) => t !== tag) }));

  const handleQuickMediaUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    kind: "image" | "video",
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxMb = kind === "image" ? 5 : 20;
    if (file.size > maxMb * 1024 * 1024) {
      setQuickError(
        `${kind === "image" ? "Image" : "Video"} size should be less than ${maxMb}MB`,
      );
      return;
    }
    if (!file.type.startsWith(`${kind}/`)) {
      setQuickError(`Please upload a ${kind} file`);
      return;
    }
    setQuickError("");
    const reader = new FileReader();
    reader.onload = () =>
      setQuickForm((p) => ({
        ...p,
        image: kind === "image" ? (reader.result as string) : "",
        video: kind === "video" ? (reader.result as string) : "",
      }));
    reader.readAsDataURL(file);
  };

  const createQuickItem = async () => {
    if (mappingPrompt?.kind !== "create") return;
    const entity = mappingPrompt.entity;
    const label = entity === "campaign" ? "Campaign" : "Event";

    const title = quickForm.title.trim();
    const start = quickForm.startDate || getLocalToday();

    if (!title) {
      setQuickError(`${label} title is required.`);
      return;
    }
    if (quickForm.endDate && quickForm.endDate < start) {
      setQuickError("End date cannot be before the start date.");
      return;
    }

    const tenantId = getTenantId();
    if (!tenantId) {
      setQuickError("Login session expired. Please login again.");
      return;
    }
    const creatorId = getStoredUserId(userId);

    setQuickSaving(true);
    setQuickError("");

    try {
      const payload: Record<string, any> = {
        title,
        description: quickForm.description,
        notes: quickForm.notes,
        start_date: new Date(start).toISOString(),
        end_date: quickForm.endDate
          ? new Date(quickForm.endDate).toISOString()
          : null,
        created_by: creatorId,
        updated_by: creatorId,
        status:
          quickForm.status || (entity === "campaign" ? "Active" : "Scheduled"),
        tenant_id: tenantId,
        tags: quickForm.tags,
      };
      if (entity === "campaign") {
        payload.image_url = quickForm.image || "";
        payload.video_url = quickForm.video || "";
      }

      const url =
        entity === "campaign"
          ? `${API_V1_BASE_URL}/campaigns/create`
          : `${API_ORIGIN}/events`;

      const res = await fetch(url, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok || result.success === false) {
        throw new Error(result.message || `Failed to create ${label}`);
      }

      const created = result.data ?? result;
      const newId = String(
        entity === "campaign"
          ? created.campaign_id
          : (created.event_id ?? created.id),
      );
      const newOption: MappingOption = {
        id: newId,
        label: created.title ?? title,
      };

      const linkWarnings: string[] = [];

      // Campaign: link selected events + assets (same endpoints as CampaignsAndEvents)
      if (entity === "campaign") {
        if (quickForm.eventIds.length > 0) {
          const r = await fetch(`${API_ROOT_URL}/campaign-events`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({
              campaign_id: newId,
              event_ids: quickForm.eventIds,
              created_by: creatorId,
            }),
          });
          if (!r.ok) linkWarnings.push("events");
        }
        if (quickForm.assetIds.length > 0) {
          const r = await fetch(`${API_ROOT_URL}/campaign-assets`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({
              campaign_id: newId,
              asset_ids: quickForm.assetIds,
              created_by: creatorId,
            }),
          });
          if (!r.ok) linkWarnings.push("assets");
        }
      }

      // Event: optionally link to campaigns (unchanged behaviour)
      if (entity === "event" && quickForm.linkCampaignIds.length > 0) {
        const linkResults = await Promise.all(
          quickForm.linkCampaignIds.map((campaignId) =>
            fetch(`${API_ROOT_URL}/campaign-events`, {
              method: "POST",
              headers: authHeaders(),
              body: JSON.stringify({
                campaign_id: campaignId,
                event_ids: [newId],
                created_by: creatorId,
              }),
            }).then((r) => r.ok),
          ),
        );
        if (linkResults.some((ok) => !ok)) linkWarnings.push("campaigns");
      }

      if (linkWarnings.length > 0) {
        setStatusMessage({
          text: `${label} created, but linking ${linkWarnings.join(" / ")} failed. You can link manually.`,
          type: "error",
        });
      }

      // Local link map update so the pickers stay in sync before the refetch lands
      if (entity === "campaign" && quickForm.eventIds.length > 0) {
        setEventCampaignMap((prev) => {
          const next = { ...prev };
          quickForm.eventIds.forEach((eid) => {
            next[eid] = [...(next[eid] || []), newId];
          });
          return next;
        });
      }
      if (entity === "event" && quickForm.linkCampaignIds.length > 0) {
        setEventCampaignMap((prev) => ({
          ...prev,
          [newId]: [...quickForm.linkCampaignIds],
        }));
      }
      void loadMappingOptions();

      // Update options + selection, then return to the confirmation summary
      let nextCampaignOpts = campaignOptions;
      let nextEventOpts = eventOptions;
      let nextCampaignIds = selectedCampaignIds;
      let nextEventIds = selectedEventIds;

      if (entity === "campaign") {
        nextCampaignOpts = [newOption, ...campaignOptions];
        nextCampaignIds = [...selectedCampaignIds, newId];
        setCampaignOptions(nextCampaignOpts);
        setSelectedCampaignIds(nextCampaignIds);
      } else {
        nextEventOpts = [newOption, ...eventOptions];
        nextEventIds = [...selectedEventIds, newId];
        setEventOptions(nextEventOpts);
        setSelectedEventIds(nextEventIds);
      }

      setQuickForm(makeEmptyQuickForm());
      const opened = await openMappingPrompt(
        nextCampaignIds,
        nextEventIds,
        nextCampaignOpts,
        nextEventOpts,
      );
      if (!opened) setMappingPrompt(null);
    } catch (err: any) {
      console.error(`Quick create ${entity} failed`, err);
      setQuickError(err?.message || `Failed to create ${label}.`);
    } finally {
      setQuickSaving(false);
    }
  };

  // Runs after the user confirms the prompt (this is the original startUpload body)
  const beginUpload = async () => {
    mappingRef.current = {
      campaignIds: [...selectedCampaignIds],
      eventIds: [...selectedEventIds],
    };
    setMappingPrompt(null);
    setShowFloatingPopup(true);

    if (showHistory) {
      setActiveTab("history");
    }

    await handleUploadAll();
  };

  const handleUploadAll = async () => {
    let pendingFiles = uploadFiles.filter((f) => f.status === "pending");

    if (pendingFiles.length === 0) {
      return;
    }

    setIsUploading(true);
    onEvent?.({ type: "start", timestamp: new Date() });

    const successfulUploads: UploadFile[] = [];

    for (let i = 0; i < pendingFiles.length; i += config.maxConcurrentUploads) {
      const currentPendingFiles = uploadFiles.filter(
        (f) => f.status === "pending",
      );
      const batch = currentPendingFiles.slice(
        i,
        i + config.maxConcurrentUploads,
      );

      const batchPromises = batch.map((file) => uploadWithRetry(file));
      const results = await Promise.allSettled(batchPromises);

      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          successfulUploads.push(result.value);
          onEvent?.({
            type: "complete",
            fileId: result.value.id,
            timestamp: new Date(),
          });
        } else {
          const failedFile = batch[index];
          onUploadError?.(result.reason, failedFile);
          onEvent?.({
            type: "error",
            fileId: failedFile.id,
            message: result.reason.message,
            timestamp: new Date(),
          });
        }
      });
    }

    setIsUploading(false);

    if (successfulUploads.length > 0) {
      setStatusMessage({
        text: `✓ Successfully uploaded ${successfulUploads.length} file(s)`,
        type: "success",
      });
      setTimeout(() => setStatusMessage(null), 3000);
      resetMappingSelection();
      onUploadComplete?.(successfulUploads);
    }

    setTimeout(() => {
      const remainingActive = uploadFiles.some(
        (f) =>
          f.status === "uploading" ||
          f.status === "pending" ||
          f.status === "processing",
      );
      if (!remainingActive) {
        setShowFloatingPopup(false);
      }
    }, 3000);
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files) return;

    const newFiles: UploadFile[] = [];
    const rejectedFiles: UploadFile[] = [];
    const currentTotalSize = validFiles.reduce((sum, f) => sum + f.size, 0);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const validation = validateFile(file);

      if (!validation.isValid) {
        const rejectedFile: UploadFile = {
          id: generateFileId(),
          assetId: crypto.randomUUID(),
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          status: "unsupported",
          progress: 0,
          uploadedChunks: 0,
          totalChunks: 1,
          rejectionReason: validation.error,
          rejectionDetails: validation.rejectionDetails,
          metadata: {
            originalName: file.name,
            lastModified: file.lastModified,
            mimeType: file.type,
          },
        };
        rejectedFiles.push(rejectedFile);

        setStatusMessage({
          text: `❌ ${validation.error}: ${file.name}`,
          type: "error",
        });
        onUploadError?.(new Error(validation.error!));
        setTimeout(() => setStatusMessage(null), 5000);
        continue;
      }

      if (currentTotalSize + file.size > config.maxTotalSize) {
        const rejectedFile: UploadFile = {
          id: generateFileId(),
          assetId: crypto.randomUUID(),
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          status: "unsupported",
          progress: 0,
          uploadedChunks: 0,
          totalChunks: 1,
          rejectionReason: `Total size limit exceeded`,
          rejectionDetails: {
            code: "TOTAL_SIZE_EXCEEDED",
            message: `Adding "${file.name}" would exceed the total upload limit of ${formatFileSize(config.maxTotalSize)}.`,
            suggestion: `Please remove some files or upload in batches.`,
          },
          metadata: {
            originalName: file.name,
            lastModified: file.lastModified,
            mimeType: file.type,
          },
        };
        rejectedFiles.push(rejectedFile);

        setStatusMessage({
          text: `Total upload size would exceed ${formatFileSize(config.maxTotalSize)} limit`,
          type: "error",
        });
        setTimeout(() => setStatusMessage(null), 3000);
        continue;
      }

      newFiles.push({
        id: generateFileId(),
        assetId: crypto.randomUUID(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        status: "pending",
        progress: 0,
        uploadedChunks: 0,
        totalChunks: 1,
        metadata: {
          originalName: file.name,
          lastModified: file.lastModified,
          mimeType: file.type,
        },
      });
    }

    if (newFiles.length > 0) {
      uploadStore.addFiles(newFiles);
      onFileAdded?.(newFiles);
      setStatusMessage({
        text: `✓ ${newFiles.length} file(s) added to upload queue`,
        type: "success",
      });
      setTimeout(() => setStatusMessage(null), 3000);
    }

    if (rejectedFiles.length > 0) {
      uploadStore.addFiles(rejectedFiles);
      setStatusMessage({
        text: `⚠️ ${rejectedFiles.length} file(s) were rejected due to validation errors`,
        type: "error",
      });
    }
  };

  const retryUnsupportedFile = (file: UploadFile) => {
    uploadStore.removeFile(file.id);

    const retryFile: UploadFile = {
      ...file,
      id: generateFileId(),
      status: "pending",
      progress: 0,
      error: undefined,
      rejectionReason: undefined,
      rejectionDetails: undefined,
    };

    uploadStore.addFiles([retryFile]);

    setStatusMessage({
      text: `Retrying upload for: ${file.name}`,
      type: "info",
    });
    setTimeout(() => setStatusMessage(null), 3000);
  };

  const cancelUpload = (fileId: string) => {
    const controller = abortControllers.current.get(fileId);
    if (controller) {
      controller.abort();
      abortControllers.current.delete(fileId);
    }

    fetch(`/api/v1/assets/progress/${fileId}`, { method: "DELETE" }).catch(
      console.error,
    );
    uploadStore.updateFile(fileId, { status: "cancelled" });
    onFileRemoved?.(fileId);
    onEvent?.({ type: "cancel", fileId, timestamp: new Date() });
  };

  const removeFile = (fileId: string) => {
    if (isUploading) {
      cancelUpload(fileId);
    }
    uploadStore.removeFile(fileId);
    onFileRemoved?.(fileId);
  };

  const fetchUploadJobs = async () => {
    if (!showHistory) return;
    await refreshJobs();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  // Initial jobs fetch
  useEffect(() => {
    fetchUploadJobs();
  }, []);

  // Load campaign / event options and the campaign <-> event links
  const loadMappingOptions = useCallback(async () => {
    const tenantId = getTenantId();
    if (!tenantId) return;

    try {
      const res = await fetch(
        `${linkTreeEndpoint}?tenant_id=${encodeURIComponent(tenantId)}`,
        { headers: authHeaders() },
      );
      if (!res.ok) throw new Error(`Request failed with ${res.status}`);
      const tree = (await res.json()).data;

      setCampaignOptions(
        (tree.campaigns || []).map((c: any) => ({
          id: String(c.campaign_id),
          label: c.title ?? String(c.campaign_id),
        })),
      );
      setEventOptions(
        (tree.events || []).map((e: any) => ({
          id: String(e.event_id),
          label: e.title ?? String(e.event_id),
        })),
      );
      const map: Record<string, string[]> = {};
      (tree.links || []).forEach((l: any) => {
        const eid = String(l.event_id);
        map[eid] = [...(map[eid] || []), String(l.campaign_id)];
      });
      setEventCampaignMap(map);
      return;
    } catch (err) {
      console.error("Failed to load campaign/event tree, using flat lists", err);
    }

    // Fallback: flat lists without link data
    try {
      const [cRes, eRes] = await Promise.all([
        fetch(
          `${campaignsEndpoint}?tenant_id=${encodeURIComponent(tenantId)}`,
        ),
        fetch(`${eventsEndpoint}?tenant_id=${encodeURIComponent(tenantId)}`),
      ]);
      const cJson = await cRes.json();
      const eJson = await eRes.json();

      setCampaignOptions(
        (cJson.data || []).map((c: any) => ({
          id: String(c.campaign_id ?? c.id),
          label: c.title ?? c.name ?? String(c.campaign_id),
        })),
      );
      setEventOptions(
        (eJson.data || []).map((e: any) => ({
          id: String(e.event_id ?? e.id),
          label: e.title ?? e.name ?? String(e.event_id ?? e.id),
        })),
      );
    } catch (err) {
      console.error("Failed to load campaigns/events for mapping", err);
    }
  }, [linkTreeEndpoint, campaignsEndpoint, eventsEndpoint]);

  useEffect(() => {
    loadMappingOptions();
  }, [loadMappingOptions]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllers.current.forEach((controller) => controller.abort());
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  return (
    <div className={`asset-ingestion ${className}`}>
      {showFloatingPopup && (
        <FloatingProgressPopup
          files={uploadFiles}
          onExpand={() => setActiveTab("history")}
          onCancel={cancelUpload}
          onClose={() => setShowFloatingPopup(false)}
        />
      )}

      <div className="flex gap-2 border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab("upload")}
          className={`px-6 py-3 font-semibold transition-all duration-200 ${
            activeTab === "upload"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          📤 Upload Files
          {uploadFiles.filter((f) => f.status === "pending").length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">
              {uploadFiles.filter((f) => f.status === "pending").length}
            </span>
          )}
          {unsupportedFiles.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">
              ⚠️ {unsupportedFiles.length}
            </span>
          )}
        </button>
        {showHistory && (
          <button
            onClick={() => setActiveTab("history")}
            className={`px-6 py-3 font-semibold transition-all duration-200 ${
              activeTab === "history"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            📋 Upload Status
            {displayJobs.filter(
              (j) => j.status === "uploading" || j.status === "processing",
            ).length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-600 text-xs rounded-full animate-pulse">
                {
                  displayJobs.filter(
                    (j) =>
                      j.status === "uploading" || j.status === "processing",
                  ).length
                }
              </span>
            )}
          </button>
        )}
      </div>

      {activeTab === "upload" && (
        <div className="space-y-6">
          {statusMessage && (
            <div
              className={`p-4 rounded-lg ${
                statusMessage.type === "success"
                  ? "bg-green-50 text-green-800 border border-green-200"
                  : statusMessage.type === "error"
                    ? "bg-red-50 text-red-800 border border-red-200"
                    : "bg-blue-50 text-blue-800 border border-blue-200"
              }`}
            >
              {statusMessage.text}
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-1">
              Map to campaigns / events{" "}
              <span className="text-xs font-normal text-gray-400">
                (optional)
              </span>
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              Assets uploaded in this batch will be linked to your selection
              once the upload succeeds. Choosing an event fills in its
              campaign; choosing a campaign shows only its events.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MultiSelectDropdown
                label="Campaigns"
                placeholder="Select campaigns"
                options={campaignOptions}
                selected={selectedCampaignIds}
                onChange={handleCampaignSelection}
                disabled={isUploading}
                note={mappingNote?.field === "campaign" ? mappingNote.text : null}
              />
              <MultiSelectDropdown
                label="Events"
                placeholder={
                  selectedCampaignIds.length && visibleEventOptions.length === 0
                    ? "No events in the selected campaigns"
                    : "Select events"
                }
                options={visibleEventOptions}
                selected={selectedEventIds}
                onChange={handleEventSelection}
                disabled={isUploading}
                note={mappingNote?.field === "event" ? mappingNote.text : null}
              />
            </div>
          </div>

          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
              isDragging
                ? "border-blue-500 bg-blue-50"
                : "border-gray-300 hover:border-blue-400 bg-white"
            } ${dropzoneClassName}`}
          >
            <input
              type="file"
              ref={fileInputRef}
              multiple={allowMultiple}
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
              accept={[
                ...Object.keys(config.allowedFileTypes),
                ...Object.values(config.allowedFileTypes),
              ].join(",")}
            />
            <div className="text-7xl mb-4">📁</div>
            <p className="text-gray-600 mb-2">Drag and drop files here, or</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition"
              disabled={isUploading}
            >
              Browse Files
            </button>
            <p className="text-xs text-gray-500 mt-4">
              🔒 Files are scanned for malware before upload
              <br />
              Max file size: {formatFileSize(config.maxFileSize)} | Total limit:{" "}
              {formatFileSize(config.maxTotalSize)}
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Supported formats:{" "}
              {Object.values(config.allowedFileTypes).join(", ")}
            </p>
          </div>

          <UnsupportedFilesSection
            files={unsupportedFiles}
            onRemove={removeFile}
            onRetry={retryUnsupportedFile}
          />

          {uploadFiles.filter((f) => f.status === "pending").length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="font-semibold text-gray-800">
                  Upload Queue (
                  {uploadFiles.filter((f) => f.status === "pending").length}{" "}
                  files)
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      uploadStore.getFiles().forEach((f) => {
                        if (f.status === "pending")
                          uploadStore.removeFile(f.id);
                      })
                    }
                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={startUpload}
                    className="px-4 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-semibold hover:shadow-lg transition"
                  >
                    Start Upload (
                    {uploadFiles.filter((f) => f.status === "pending").length})
                  </button>
                </div>
              </div>

              <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                {uploadFiles
                  .filter((f) => f.status === "pending")
                  .map((file) => (
                    <div
                      key={file.id}
                      className="p-4 hover:bg-gray-50 transition"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="text-2xl">
                              {file.type.startsWith("image/") && "🖼️"}
                              {file.type.startsWith("video/") && "🎥"}
                              {file.type.startsWith("audio/") && "🎵"}
                              {file.type === "application/pdf" && "📄"}
                              {file.type === "text/plain" && "📃"}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-800 truncate max-w-md">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(file.size)}
                              </p>
                            </div>
                          </div>
                          <div className="mt-2">
                            <span className="text-xs text-gray-500">
                              ⏳ Pending
                            </span>
                          </div>
                        </div>

                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => removeFile(file.id)}
                            className="text-red-500 hover:text-red-700 text-xl leading-none"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {uploadFiles.filter((f) => f.status === "pending").length === 0 &&
            uploadFiles.filter((f) => f.status !== "unsupported").length >
              0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-green-600">
                  ✓ All files have been processed!
                </p>
                <button
                  onClick={() => setActiveTab("history")}
                  className="mt-2 text-blue-600 hover:text-blue-700 font-medium"
                >
                  View Upload History →
                </button>
              </div>
            )}
        </div>
      )}

      {activeTab === "history" && showHistory && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <span>🔄</span> Upload History
              {displayJobs.filter(
                (j) => j.status === "uploading" || j.status === "processing",
              ).length > 0 && (
                <span className="text-xs bg-yellow-100 text-yellow-600 px-2 py-1 rounded-full animate-pulse">
                  {
                    displayJobs.filter(
                      (j) =>
                        j.status === "uploading" || j.status === "processing",
                    ).length
                  }{" "}
                  active
                </span>
              )}
            </h3>
          </div>

          {displayJobs.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-6xl mb-4">📭</div>
              <p className="text-gray-500">No upload history yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Uploaded files will appear here
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {displayJobs.map((job) => {
                const activeUpload = uploadFiles.find(
                  (f) =>
                    f.id === job.id &&
                    (f.status === "uploading" ||
                      f.status === "pending" ||
                      f.status === "processing"),
                );
                const failedUpload = uploadFiles.find(
                  (f) => f.id === job.id && f.status === "error",
                );
                const displayProgress = activeUpload?.progress || job.progress;
                let displayStatus = job.status;

                if (activeUpload) {
                  if (activeUpload.status === "uploading")
                    displayStatus = "uploading";
                  else if (activeUpload.status === "processing")
                    displayStatus = "processing";
                  else if (activeUpload.status === "pending")
                    displayStatus = "queued";
                }

                return (
                  <div key={job.id} className="p-4 hover:bg-gray-50 transition">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-gray-800">
                            {job.originalName}
                          </p>
                          <span className="text-xs text-gray-400">
                            {formatFileSize(job.fileSize)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          {new Date(job.createdAt).toLocaleString()}
                        </p>

                        {(displayStatus === "uploading" ||
                          displayStatus === "processing" ||
                          displayStatus === "queued") && (
                          <div className="mt-2">
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-gray-500">
                                {displayStatus === "uploading" &&
                                  "Uploading to server..."}
                                {displayStatus === "processing" &&
                                  (job.metadata?.processingStep ||
                                    "Processing file...")}
                                {displayStatus === "queued" && "Waiting..."}
                              </span>
                              {displayStatus !== "queued" && (
                                <span className="text-blue-600 font-semibold">
                                  {Math.round(displayProgress)}%
                                </span>
                              )}
                            </div>
                            <div className="bg-gray-200 rounded-full h-1.5 overflow-hidden">
                              <div
                                className={`h-full transition-all duration-300 ${
                                  displayStatus === "processing"
                                    ? "bg-orange-500"
                                    : displayStatus === "uploading"
                                      ? "bg-gradient-to-r from-blue-500 to-purple-600"
                                      : "bg-gray-400"
                                }`}
                                style={{ width: `${displayProgress}%` }}
                              />
                            </div>
                            {job.metadata?.processingMessage &&
                              displayStatus === "processing" && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {job.metadata.processingMessage}
                                </p>
                              )}
                          </div>
                        )}

                        {job.error && (
                          <p className="text-xs text-red-600 mt-1">
                            {job.error}
                          </p>
                        )}
                      </div>
                      <div>
                        {displayStatus === "completed" && (
                          <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                            ✓ Completed
                          </span>
                        )}
                        {displayStatus === "processing" && (
                          <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full animate-pulse">
                            ⚙️ Processing
                          </span>
                        )}
                        {displayStatus === "uploading" && (
                          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full animate-pulse">
                            📤 Uploading
                          </span>
                        )}
                        {displayStatus === "queued" && (
                          <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                            ⏳ Queued
                          </span>
                        )}
                        {displayStatus === "failed" && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">
                              ❌ Failed
                            </span>
                            {failedUpload && (
                              <button
                                type="button"
                                onClick={() => retryFailedFile(failedUpload)}
                                disabled={isUploading}
                                className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                              >
                                Retry Upload
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {job.outputUrl && displayStatus === "completed" && (
                      <div className="mt-2">
                        <a
                          href={job.outputUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-500 hover:text-blue-700"
                        >
                          View File →
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {mappingPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`bg-white rounded-xl w-full max-h-[90vh] overflow-y-auto shadow-2xl ${
              mappingPrompt.kind === "create"
                ? mappingPrompt.entity === "campaign"
                  ? "max-w-4xl"
                  : "max-w-2xl"
                : "max-w-lg"
            }`}
          >
            {mappingPrompt.kind === "create" ? (
              <>
                <div className="p-6 space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      Create New{" "}
                      {mappingPrompt.entity === "campaign"
                        ? "Campaign"
                        : "Event"}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Your uploaded asset(s) will be mapped to it automatically.
                    </p>
                  </div>

                  {quickError && (
                    <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                      {quickError}
                    </div>
                  )}

                  <div
                    className={
                      mappingPrompt.entity === "campaign"
                        ? "grid grid-cols-1 lg:grid-cols-2 gap-6"
                        : "space-y-4"
                    }
                  >
                    {/* ---------- LEFT COLUMN ---------- */}
                    <div className="space-y-4">
                      {mappingPrompt.entity === "campaign" && (
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Media
                          </label>
                          <div className="flex gap-2 mb-3">
                            <button
                              type="button"
                              onClick={() => setQuickMediaTab("image")}
                              className={`px-4 py-2 rounded text-sm transition ${
                                quickMediaTab === "image"
                                  ? "bg-blue-600 text-white"
                                  : "bg-gray-200"
                              }`}
                            >
                              📷 Image
                            </button>
                            <button
                              type="button"
                              onClick={() => setQuickMediaTab("video")}
                              className={`px-4 py-2 rounded text-sm transition ${
                                quickMediaTab === "video"
                                  ? "bg-blue-600 text-white"
                                  : "bg-gray-200"
                              }`}
                            >
                              🎥 Video
                            </button>
                          </div>
                          {quickMediaTab === "image" ? (
                            <div
                              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500"
                              onClick={() => quickImageRef.current?.click()}
                            >
                              {quickForm.image ? (
                                <img
                                  src={quickForm.image}
                                  alt="Preview"
                                  className="max-h-40 mx-auto rounded"
                                />
                              ) : (
                                <p className="text-gray-500 text-sm">
                                  Click to upload image
                                </p>
                              )}
                              <input
                                ref={quickImageRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) =>
                                  handleQuickMediaUpload(e, "image")
                                }
                              />
                            </div>
                          ) : (
                            <div
                              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500"
                              onClick={() => quickVideoRef.current?.click()}
                            >
                              {quickForm.video ? (
                                <video
                                  src={quickForm.video}
                                  className="max-h-40 mx-auto rounded"
                                  controls
                                />
                              ) : (
                                <p className="text-gray-500 text-sm">
                                  Click to upload video
                                </p>
                              )}
                              <input
                                ref={quickVideoRef}
                                type="file"
                                accept="video/*"
                                className="hidden"
                                onChange={(e) =>
                                  handleQuickMediaUpload(e, "video")
                                }
                              />
                            </div>
                          )}
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {mappingPrompt.entity === "campaign"
                            ? "Title"
                            : "Event Name"}{" "}
                          *
                        </label>
                        <input
                          type="text"
                          autoFocus
                          value={quickForm.title}
                          onChange={(e) =>
                            setQuickForm((p) => ({
                              ...p,
                              title: e.target.value,
                            }))
                          }
                          disabled={quickSaving}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          placeholder={
                            mappingPrompt.entity === "campaign"
                              ? "Campaign title"
                              : "Event name"
                          }
                        />
                      </div>

                      {mappingPrompt.entity === "event" && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Event Date
                            </label>
                            <input
                              type="date"
                              value={quickForm.startDate}
                              onChange={(e) =>
                                setQuickForm((p) => ({
                                  ...p,
                                  startDate: e.target.value,
                                }))
                              }
                              disabled={quickSaving}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              End Date
                            </label>
                            <input
                              type="date"
                              min={quickForm.startDate || undefined}
                              value={quickForm.endDate}
                              onChange={(e) =>
                                setQuickForm((p) => ({
                                  ...p,
                                  endDate: e.target.value,
                                }))
                              }
                              disabled={quickSaving}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                        </div>
                      )}

                      {mappingPrompt.entity === "campaign" ? (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Notes
                            </label>
                            <textarea
                              rows={3}
                              value={quickForm.notes}
                              onChange={(e) =>
                                setQuickForm((p) => ({
                                  ...p,
                                  notes: e.target.value,
                                }))
                              }
                              disabled={quickSaving}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              placeholder="Additional notes"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Description
                            </label>
                            <textarea
                              rows={2}
                              value={quickForm.description}
                              onChange={(e) =>
                                setQuickForm((p) => ({
                                  ...p,
                                  description: e.target.value,
                                }))
                              }
                              disabled={quickSaving}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              placeholder="Short description"
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Description
                            </label>
                            <textarea
                              rows={2}
                              value={quickForm.description}
                              onChange={(e) =>
                                setQuickForm((p) => ({
                                  ...p,
                                  description: e.target.value,
                                }))
                              }
                              disabled={quickSaving}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Notes
                            </label>
                            <textarea
                              rows={3}
                              value={quickForm.notes}
                              onChange={(e) =>
                                setQuickForm((p) => ({
                                  ...p,
                                  notes: e.target.value,
                                }))
                              }
                              disabled={quickSaving}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              placeholder="Optional notes"
                            />
                          </div>
                        </>
                      )}
                    </div>

                    {/* ---------- RIGHT COLUMN (campaign) / continuation (event) ---------- */}
                    <div className="space-y-4">
                      {mappingPrompt.entity === "campaign" && (
                        <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                          <h4 className="font-semibold text-sm">Schedule</h4>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Start Date
                            </label>
                            <input
                              type="date"
                              value={quickForm.startDate}
                              onChange={(e) =>
                                setQuickForm((p) => ({
                                  ...p,
                                  startDate: e.target.value,
                                }))
                              }
                              disabled={quickSaving}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              End Date
                            </label>
                            <input
                              type="date"
                              min={quickForm.startDate || undefined}
                              value={quickForm.endDate}
                              onChange={(e) =>
                                setQuickForm((p) => ({
                                  ...p,
                                  endDate: e.target.value,
                                }))
                              }
                              disabled={quickSaving}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                        </div>
                      )}

                      <div
                        className={
                          mappingPrompt.entity === "campaign"
                            ? "bg-gray-50 p-4 rounded-lg"
                            : ""
                        }
                      >
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Status
                        </label>
                        <select
                          value={quickForm.status}
                          onChange={(e) =>
                            setQuickForm((p) => ({
                              ...p,
                              status: e.target.value,
                            }))
                          }
                          disabled={quickSaving}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          {mappingPrompt.entity === "event" && (
                            <option value="Scheduled">Scheduled</option>
                          )}
                          <option value="Active">Active</option>
                          <option value="Archived">Archived</option>
                        </select>
                      </div>

                      <div
                        className={
                          mappingPrompt.entity === "campaign"
                            ? "bg-gray-50 p-4 rounded-lg"
                            : ""
                        }
                      >
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tags
                        </label>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {quickForm.tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-gray-200 text-gray-700 text-sm rounded-full"
                            >
                              #{tag}
                              <button
                                type="button"
                                onClick={() => removeQuickTag(tag)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={quickTag}
                            onChange={(e) => setQuickTag(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addQuickTag();
                              }
                            }}
                            disabled={quickSaving}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="Add tag..."
                          />
                          <button
                            type="button"
                            onClick={addQuickTag}
                            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                          >
                            Add
                          </button>
                        </div>
                      </div>

                      {mappingPrompt.entity === "campaign" && (
                        <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                          <MultiSelectDropdown
                            label="Link Events"
                            placeholder="No events linked"
                            options={eventOptions}
                            selected={quickForm.eventIds}
                            onChange={(ids) =>
                              setQuickForm((p) => ({ ...p, eventIds: ids }))
                            }
                            disabled={quickSaving}
                          />
                          <MultiSelectDropdown
                            label="Link Assets"
                            placeholder="No assets linked"
                            options={assetOptions}
                            selected={quickForm.assetIds}
                            onChange={(ids) =>
                              setQuickForm((p) => ({ ...p, assetIds: ids }))
                            }
                            disabled={quickSaving}
                          />
                        </div>
                      )}

                      {mappingPrompt.entity === "event" && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Link to campaigns{" "}
                            <span className="text-xs font-normal text-gray-400">
                              (optional)
                            </span>
                          </label>
                          <div className="border border-gray-300 rounded-lg max-h-32 overflow-y-auto divide-y divide-gray-100">
                            {campaignOptions.length === 0 && (
                              <p className="p-3 text-sm text-gray-400">
                                No campaigns available
                              </p>
                            )}
                            {campaignOptions.map((c) => (
                              <label
                                key={c.id}
                                className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  disabled={quickSaving}
                                  checked={quickForm.linkCampaignIds.includes(
                                    c.id,
                                  )}
                                  onChange={() =>
                                    setQuickForm((p) => ({
                                      ...p,
                                      linkCampaignIds:
                                        p.linkCampaignIds.includes(c.id)
                                          ? p.linkCampaignIds.filter(
                                              (x) => x !== c.id,
                                            )
                                          : [...p.linkCampaignIds, c.id],
                                    }))
                                  }
                                />
                                <span className="truncate">{c.label}</span>
                              </label>
                            ))}
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            Linked campaigns will also receive the uploaded
                            asset(s).
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setQuickError("");
                      openMappingPrompt(selectedCampaignIds, selectedEventIds);
                    }}
                    disabled={quickSaving}
                    className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={createQuickItem}
                    disabled={quickSaving}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {quickSaving
                      ? "Creating..."
                      : `Create ${
                          mappingPrompt.entity === "campaign"
                            ? "Campaign"
                            : "Event"
                        }`}
                  </button>
                </div>
              </>
            ) : (
              <>
                {mappingPrompt.kind === "none" ? (
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">
                      No Campaign or Event Selected
                    </h3>
                    <p className="text-sm text-gray-600">
                      You have not selected any campaign or event. The asset(s)
                      you are about to upload will not be associated with any
                      campaign or event at this time. You may associate them
                      later through the relevant campaign or event management
                      screen.
                    </p>
                    <p className="text-sm text-gray-600 mt-3">
                      By selecting “Continue Without Mapping”, you acknowledge
                      and accept this.
                    </p>
                  </div>
                ) : (
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">
                      Confirm Asset Mapping
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      By proceeding, you authorise the uploaded asset(s) to be
                      associated with the following:
                    </p>

                    {mappingPrompt.campaigns.length > 0 && (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-800">
                          Campaigns (selected directly)
                        </p>
                        <ul className="list-disc ml-5 text-sm text-gray-600">
                          {mappingPrompt.campaigns.map((c) => (
                            <li key={c.id}>{c.label}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {mappingPrompt.eventLinks.length > 0 && (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-800">
                          Events and their linked campaigns
                        </p>
                        <ul className="ml-1 text-sm text-gray-600 space-y-2 mt-1">
                          {mappingPrompt.eventLinks.map(
                            ({ event, campaigns }) => (
                              <li key={event.id}>
                                <span className="font-medium">
                                  {event.label}
                                </span>
                                {campaigns.length > 0 ? (
                                  <>
                                    {" "}
                                    is linked to campaign(s):{" "}
                                    {campaigns.map((c) => c.label).join(", ")}.
                                    The asset(s) will also be mapped to{" "}
                                    {campaigns.length === 1
                                      ? "this campaign"
                                      : "these campaigns"}
                                    .
                                  </>
                                ) : (
                                  <>
                                    {" "}
                                    is not linked to any campaign, so no mapping
                                    will be created for it.
                                  </>
                                )}
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Quick create entry points */}
                <div className="px-6 pb-4">
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3 flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-xs text-gray-500">
                      Need a new one for this upload?
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => openQuickCreate("campaign")}
                        className="px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition"
                      >
                        + Create Campaign
                      </button>
                      <button
                        type="button"
                        onClick={() => openQuickCreate("event")}
                        className="px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition"
                      >
                        + Create Event
                      </button>
                    </div>
                  </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                  <button
                    onClick={() => setMappingPrompt(null)}
                    className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Go Back
                  </button>
                  <button
                    onClick={beginUpload}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {mappingPrompt.kind === "none"
                      ? "Continue Without Mapping"
                      : "Confirm & Upload"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showMetadataPanel && selectedFileMetadata && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-800">
                File Details
              </h3>
              <button
                onClick={() => setShowMetadataPanel(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    File Name
                  </label>
                  <p className="text-gray-800">{selectedFileMetadata.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    File Size
                  </label>
                  <p className="text-gray-800">
                    {formatFileSize(selectedFileMetadata.size)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    File Type
                  </label>
                  <p className="text-gray-800">{selectedFileMetadata.type}</p>
                </div>
                {selectedFileMetadata.uploadedUrl && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      File URL
                    </label>
                    <p className="text-sm text-blue-600 break-all">
                      <a
                        href={selectedFileMetadata.uploadedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {selectedFileMetadata.uploadedUrl}
                      </a>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
                .asset-ingestion { width: 100%; }
                @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
                .animate-pulse { animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slide-in-from-bottom-4 {
                    from { transform: translateY(1rem); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .animate-in {
                    animation-duration: 0.2s;
                    animation-fill-mode: both;
                }
                .fade-in { animation-name: fade-in; }
                .slide-in-from-bottom-4 { animation-name: slide-in-from-bottom-4; }
            `}</style>
    </div>
  );
};

export default AssetIngestion;